/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

// ============================================
// AHG QUICKBUY MODAL
// Intercepts Quick Buy clicks and opens a popup
// instead of the default inline expanding panel.
// ============================================
(function () {
  // Only run if quickbuy is enabled
  document.addEventListener('DOMContentLoaded', function () {
    if (typeof theme === 'undefined' || !theme.settings || theme.settings.quickbuyStyle === 'off') return;

    // ------------------------------------------
    // Build modal DOM
    // ------------------------------------------
    var modal = document.createElement('div');
    modal.id = 'ahg-qb-modal';
    modal.innerHTML =
      '<div class="ahg-qb-window">' +
        '<button class="ahg-qb-close" aria-label="Close">&#x2715;</button>' +
        '<div class="ahg-qb-body"></div>' +
      '</div>';
    document.body.appendChild(modal);

    var currentAbort = null;

    // ------------------------------------------
    // Open modal and load product content
    // ------------------------------------------
    function openModal(productUrl) {
      var body = modal.querySelector('.ahg-qb-body');
      body.innerHTML = '<div class="ahg-qb-spinner"><div class="loading-spinner"></div></div>';
      modal.classList.add('is-open');
      document.body.classList.add('ahg-qb-open');

      if (currentAbort) currentAbort.abort();
      currentAbort = new AbortController();

      fetch(productUrl, { signal: currentAbort.signal })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var tmpl = document.createElement('template');
          tmpl.innerHTML = html;
          var detail = tmpl.content.querySelector('.quickbuy-content');
          if (!detail) { closeModal(); return; }

          // Apply same content transforms as main.js quickbuy
          detail.querySelectorAll('.more').forEach(function (el) { el.href = productUrl; });
          detail.querySelectorAll('.product-title').forEach(function (el) {
            el.innerHTML = '<a href="' + productUrl + '">' + el.innerHTML + '</a>';
          });
          detail.querySelectorAll('.show-gallery').forEach(function (el) {
            el.classList.remove('show-gallery');
            el.href = productUrl;
          });
          detail.querySelectorAll('.not-in-quickbuy').forEach(function (el) { el.remove(); });
          detail.querySelectorAll('.only-in-quickbuy').forEach(function (el) { el.classList.remove('only-in-quickbuy'); });
          detail.querySelectorAll('.sticky-content-container').forEach(function (el) { el.classList.remove('sticky-content-container'); });
          detail.querySelectorAll('pickup-availability').forEach(function (el) { el.remove(); });
          detail.querySelectorAll('variant-picker[data-update-url="true"]').forEach(function (el) { el.dataset.updateUrl = 'false'; });
          detail.querySelectorAll('noscript').forEach(function (el) { el.remove(); });
          ['media-gallery--layout-carousel-beside', 'media-gallery--layout-columns-1',
           'media-gallery--layout-columns-2', 'media-gallery--layout-collage-1',
           'media-gallery--layout-collage-2'].forEach(function (cl) {
            detail.querySelectorAll('.' + cl).forEach(function (el) {
              el.classList.remove(cl);
              el.classList.add('media-gallery--layout-carousel-under');
            });
          });
          detail.querySelectorAll('.product-media-collage').forEach(function (el) { el.remove(); });
          detail.querySelectorAll('.media-gallery .main-image carousel-slider').forEach(function (el) { el.classList.remove('mobile-only'); });
          detail.querySelectorAll('.media-gallery .thumbnails').forEach(function (el) { el.classList.remove('mobile-only'); });

          // Add external stylesheets referenced by the product section
          tmpl.content.querySelectorAll('.section-main-product link[rel="stylesheet"]').forEach(function (el) {
            if (!document.querySelector('link[rel="stylesheet"][href="' + el.getAttribute('href') + '"]')) {
              document.head.insertAdjacentHTML('beforeend', el.outerHTML);
            }
          });

          // Add external scripts referenced by the product section (e.g. variant-picker.js)
          // Without this, custom elements like <variant-picker> are never defined on pages
          // that don't load the product JS (e.g. collection pages), so variant selection
          // in the modal does nothing   clicks register visually but the selected variant
          // ID never updates and the cart always receives the default variant.
          tmpl.content.querySelectorAll('.section-main-product script[src]').forEach(function (el) {
            if (!document.querySelector('script[src="' + el.getAttribute('src') + '"]')) {
              var scr = document.createElement('script');
              scr.src = el.getAttribute('src');
              scr.defer = true;
              document.head.appendChild(scr);
            }
          });

          body.innerHTML = '';
          body.appendChild(detail);
        })
        .catch(function () {});
    }

    // ------------------------------------------
    // Close modal
    // ------------------------------------------
    function closeModal() {
      modal.classList.remove('is-open');
      document.body.classList.remove('ahg-qb-open');
      if (currentAbort) { currentAbort.abort(); currentAbort = null; }
      setTimeout(function () {
        modal.querySelector('.ahg-qb-body').innerHTML = '';
      }, 300);
    }

    // Close on backdrop click
    modal.addEventListener('click', function (e) { if (e.target === modal) closeModal(); });
    // Close on X button
    modal.querySelector('.ahg-qb-close').addEventListener('click', closeModal);
    // Close on Escape
    document.addEventListener('keyup', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeModal();
    });

    // ------------------------------------------
    // Intercept quickbuy-toggle clicks BEFORE
    // main.js handles them (capture phase)
    // ------------------------------------------
    document.addEventListener('click', function (evt) {
      var toggle = evt.target.closest('.quickbuy-toggle');
      if (!toggle) return;
      if (window.innerWidth < 768) return; // mobile: navigate to product page normally
      evt.preventDefault();
      evt.stopImmediatePropagation();
      openModal(toggle.href);
    }, true);
  });
}());
